import { z } from 'zod';
import { PROVIDERS } from './capabilities.ts';
import { PAYMENT_STATUSES, REGISTRATION_STATUSES } from './payment.ts';

/**
 * Filter AST used by segments, smart folders, search and campaign audiences.
 * Users never send SQL: the server validates this tree and compiles it into
 * parameterised SQL. Scope-switch nodes guarantee that every condition inside
 * one `registration` node is evaluated against the SAME registration row
 * (e.g. U12 AND outstanding > 0 can never match U12 from one registration and
 * outstanding from another).
 */
export const TARGET_TYPES = ['contact', 'conversation', 'registration'] as const;
export type TargetType = (typeof TARGET_TYPES)[number];
export type FieldScope = TargetType;

export const COMPARATORS = [
  'eq',
  'neq',
  'in',
  'not_in',
  'contains',
  'starts_with',
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
  'is_empty',
  'not_empty',
  'is_true',
  'is_false',
  'within_last_days',
  'older_than_days',
  'in_folder',
] as const;
export type Comparator = (typeof COMPARATORS)[number];

export type FieldValueType = 'text' | 'enum' | 'id' | 'money' | 'number' | 'boolean' | 'datetime' | 'date' | 'tags' | 'phone' | 'folder' | 'route';
export type RefType = 'event' | 'division' | 'team' | 'user' | 'channel_account' | 'campaign' | 'folder';

export interface FieldDef {
  key: string;
  scope: FieldScope;
  type: FieldValueType;
  labelTh: string;
  comparators: readonly Comparator[];
  enumValues?: readonly string[];
  refType?: RefType;
  /** Longer Thai explanation for the filter builder (optional). */
  hintTh?: string;
  /** Built by the app (e.g. the send page), never offered in the filter builder. */
  internal?: boolean;
}

/**
 * Value of `contact.send_route`: which of the chosen accounts a customer is reached through right now.
 * `prefer: 'line'` = LINE first (no 24 h limit, uses the OA's monthly quota); `'window'` = Facebook/Instagram
 * first while their reply window is open (free), then LINE. Accounts outside their window, blocked/unfollowed
 * chats and accounts not in `allowed` are never picked.
 */
export interface SendRouteValue {
  accountId: string;
  allowed: string[];
  prefer: 'line' | 'window';
  /**
   * The SMS connection to fall back to when no chat can carry the message (v1.80.0). Absent means SMS is not
   * part of this run at all: it costs money, so it is never reached for unless the shop asked for it.
   */
  smsAccountId?: string;
  /**
   * "ส่งทุกแพลตฟอร์ม" (v1.88.0): instead of picking ONE account per customer, every chosen account the customer
   * can be reached on right now carries the message. `accountId` then asks "can this account reach them?", not
   * "is this the account they are routed through?". Absent/false = the one-route rule above, unchanged.
   */
  everywhere?: boolean;
}

export const TARGET_TYPE_LABELS_TH: Record<TargetType, string> = {
  contact: 'ผู้ติดต่อ',
  conversation: 'แชท',
  registration: 'ใบสมัคร',
};

/** Thai label for scope-switch groups, e.g. "ใบสมัครเดียวกัน" (all conditions match one registration). */
export const SCOPE_LABELS_TH: Record<FieldScope, string> = {
  contact: 'ผู้ติดต่อของรายการนี้',
  conversation: 'มีแชทที่',
  registration: 'ใบสมัครเดียวกัน',
};

export const COMPARATOR_LABELS_TH: Record<Comparator, string> = {
  eq: 'เท่ากับ',
  neq: 'ไม่เท่ากับ',
  in: 'เป็นค่าใดค่าหนึ่งใน',
  not_in: 'ไม่ใช่ค่าใดใน',
  contains: 'มีคำว่า',
  starts_with: 'ขึ้นต้นด้วย',
  gt: 'มากกว่า',
  gte: 'มากกว่าหรือเท่ากับ',
  lt: 'น้อยกว่า',
  lte: 'น้อยกว่าหรือเท่ากับ',
  between: 'อยู่ระหว่าง',
  is_empty: 'ว่าง',
  not_empty: 'ไม่ว่าง',
  is_true: 'ใช่',
  is_false: 'ไม่ใช่',
  within_last_days: 'ภายใน N วันที่ผ่านมา',
  older_than_days: 'นานกว่า N วัน',
  in_folder: 'อยู่ในหมวด',
};

const TEXT: readonly Comparator[] = ['eq', 'neq', 'in', 'contains', 'starts_with', 'is_empty', 'not_empty'];
const ENUM: readonly Comparator[] = ['eq', 'neq', 'in', 'not_in'];
const ID: readonly Comparator[] = ['eq', 'neq', 'in', 'not_in', 'is_empty', 'not_empty'];
const NUM: readonly Comparator[] = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between'];
const BOOL: readonly Comparator[] = ['is_true', 'is_false'];
const DATETIME: readonly Comparator[] = ['gt', 'gte', 'lt', 'lte', 'between', 'within_last_days', 'older_than_days', 'is_empty', 'not_empty'];
const DATE: readonly Comparator[] = ['eq', 'gt', 'gte', 'lt', 'lte', 'between'];
const TAGS: readonly Comparator[] = ['contains', 'in', 'not_in', 'is_empty', 'not_empty'];

const f = (d: FieldDef): FieldDef => d;

export const FILTER_FIELDS: readonly FieldDef[] = [
  // contact
  f({ key: 'contact.display_name', scope: 'contact', type: 'text', labelTh: 'ชื่อผู้ติดต่อ', comparators: TEXT }),
  f({ key: 'contact.full_name', scope: 'contact', type: 'text', labelTh: 'ชื่อจริง', comparators: TEXT }),
  f({ key: 'contact.coach_name', scope: 'contact', type: 'text', labelTh: 'ชื่อโค้ช', comparators: TEXT }),
  f({ key: 'contact.phone', scope: 'contact', type: 'phone', labelTh: 'เบอร์โทร', comparators: ['eq', 'in', 'contains', 'is_empty', 'not_empty'] }),
  f({ key: 'contact.province', scope: 'contact', type: 'text', labelTh: 'จังหวัด', comparators: TEXT }),
  f({ key: 'contact.district', scope: 'contact', type: 'text', labelTh: 'อำเภอ', comparators: TEXT }),
  f({ key: 'contact.subdistrict', scope: 'contact', type: 'text', labelTh: 'ตำบล', comparators: TEXT }),
  f({ key: 'contact.source', scope: 'contact', type: 'text', labelTh: 'แหล่งที่มาของผู้ติดต่อ', comparators: TEXT, hintTh: 'เช่น manual, import, sync:tournament' }),
  f({ key: 'contact.owner_user_id', scope: 'contact', type: 'id', refType: 'user', labelTh: 'ผู้ดูแลผู้ติดต่อ', comparators: ID }),
  f({ key: 'contact.tags', scope: 'contact', type: 'tags', labelTh: 'แท็กผู้ติดต่อ', comparators: TAGS }),
  f({ key: 'contact.last_contact_at', scope: 'contact', type: 'datetime', labelTh: 'ติดต่อล่าสุด', comparators: DATETIME }),
  f({ key: 'contact.created_at', scope: 'contact', type: 'datetime', labelTh: 'วันที่สร้างผู้ติดต่อ', comparators: DATETIME }),
  f({ key: 'contact.team_id', scope: 'contact', type: 'id', refType: 'team', labelTh: 'ทีม', comparators: ['eq', 'in', 'not_in', 'is_empty', 'not_empty'] }),
  f({ key: 'contact.folder', scope: 'contact', type: 'folder', refType: 'folder', labelTh: 'อยู่ในหมวด', comparators: ['in_folder'] }),
  f({ key: 'contact.marketing_opted_out', scope: 'contact', type: 'boolean', labelTh: 'ยกเลิกรับข่าวสาร', comparators: BOOL }),
  f({ key: 'contact.has_channel', scope: 'contact', type: 'enum', enumValues: PROVIDERS, labelTh: 'มีบัญชีช่องทาง', comparators: ['eq', 'in', 'not_in'] }),
  // The account of the chat the customer last wrote in (active customer accounts only) — sending to a category goes
  // out through each customer's own chat, so one person is reached once, from the page/platform they actually use.
  f({ key: 'contact.reply_account', scope: 'contact', type: 'id', refType: 'channel_account', labelTh: 'บัญชีที่ลูกค้าทักมาล่าสุด', comparators: ['eq'] }),
  // When the customer last wrote in that same chat — Facebook/Instagram only allow sending within 24 h of it.
  f({ key: 'contact.last_inbound_at', scope: 'contact', type: 'datetime', labelTh: 'ลูกค้าทักมาล่าสุด', comparators: DATETIME }),
  f({ key: 'contact.send_route', scope: 'contact', type: 'route', refType: 'channel_account', labelTh: 'ส่งผ่านบัญชีนี้ (เลือกโดยระบบ)', comparators: ['eq'], internal: true }),
  f({ key: 'contact.campaign_sent', scope: 'contact', type: 'id', refType: 'campaign', labelTh: 'ส่งข้อความนี้แล้ว', comparators: ['eq', 'neq'] }),
  // registration
  f({ key: 'registration.event_id', scope: 'registration', type: 'id', refType: 'event', labelTh: 'รายการแข่ง', comparators: ID }),
  f({ key: 'registration.division_id', scope: 'registration', type: 'id', refType: 'division', labelTh: 'รุ่น', comparators: ID }),
  f({ key: 'registration.division_code', scope: 'registration', type: 'text', labelTh: 'รหัสรุ่น (เช่น U12)', comparators: ['eq', 'in', 'not_in'] }),
  f({ key: 'registration.team_id', scope: 'registration', type: 'id', refType: 'team', labelTh: 'ทีมที่สมัคร', comparators: ID }),
  f({ key: 'registration.registration_status', scope: 'registration', type: 'enum', enumValues: REGISTRATION_STATUSES, labelTh: 'สถานะสมัคร', comparators: ENUM }),
  f({ key: 'registration.payment_status', scope: 'registration', type: 'enum', enumValues: PAYMENT_STATUSES, labelTh: 'สถานะเงิน', comparators: ENUM }),
  f({ key: 'registration.outstanding_minor', scope: 'registration', type: 'money', labelTh: 'ยอดคงเหลือ', comparators: NUM }),
  f({ key: 'registration.amount_due_minor', scope: 'registration', type: 'money', labelTh: 'ยอดที่ต้องชำระ', comparators: NUM }),
  f({ key: 'registration.paid_verified_minor', scope: 'registration', type: 'money', labelTh: 'เงินที่ยืนยันแล้ว', comparators: NUM }),
  f({ key: 'registration.pending_verification_minor', scope: 'registration', type: 'money', labelTh: 'ยอดรอตรวจสอบ', comparators: NUM }),
  f({ key: 'registration.deposit_met', scope: 'registration', type: 'boolean', labelTh: 'มัดจำถึงเกณฑ์', comparators: BOOL }),
  f({ key: 'registration.deposit_required_minor', scope: 'registration', type: 'money', labelTh: 'ยอดมัดจำที่กำหนด', comparators: NUM }),
  f({
    key: 'registration.created_at',
    scope: 'registration',
    type: 'datetime',
    labelTh: 'วันที่สมัคร',
    comparators: DATETIME,
    hintTh: 'ใช้กรอง "เคยสมัครในช่วงวันที่" โดยวางไว้ในกลุ่มใบสมัครเดียวกันกับเงื่อนไขรุ่น/รายการ',
  }),
  f({ key: 'registration.due_date', scope: 'registration', type: 'date', labelTh: 'วันครบกำหนดชำระ', comparators: DATE }),
  f({ key: 'registration.event_starts_on', scope: 'registration', type: 'date', labelTh: 'วันแข่ง', comparators: DATE }),
  f({
    key: 'registration.source_system',
    scope: 'registration',
    type: 'text',
    labelTh: 'แหล่งข้อมูลใบสมัคร',
    comparators: ['eq', 'neq', 'in', 'is_empty', 'not_empty'],
    hintTh: 'tournament = natashu.com, natashu_reg = ระบบสมัครเดิม, csv = นำเข้าไฟล์, ว่าง = สร้างในระบบนี้',
  }),
  f({ key: 'registration.folder', scope: 'registration', type: 'folder', refType: 'folder', labelTh: 'ใบสมัครอยู่ในหมวด', comparators: ['in_folder'] }),
  // conversation
  f({ key: 'conversation.channel_account_id', scope: 'conversation', type: 'id', refType: 'channel_account', labelTh: 'บัญชีช่องทาง', comparators: ID }),
  f({ key: 'conversation.provider', scope: 'conversation', type: 'enum', enumValues: PROVIDERS, labelTh: 'ช่องทาง', comparators: ENUM }),
  f({ key: 'conversation.assignee_user_id', scope: 'conversation', type: 'id', refType: 'user', labelTh: 'ผู้รับผิดชอบแชท', comparators: ID }),
  f({ key: 'conversation.needs_reply', scope: 'conversation', type: 'boolean', labelTh: 'รอแอดมินตอบ', comparators: BOOL }),
  f({ key: 'conversation.status', scope: 'conversation', type: 'enum', enumValues: ['open', 'closed'], labelTh: 'สถานะแชท', comparators: ENUM }),
  f({ key: 'conversation.last_activity_at', scope: 'conversation', type: 'datetime', labelTh: 'กิจกรรมล่าสุด', comparators: DATETIME }),
  f({ key: 'conversation.last_inbound_at', scope: 'conversation', type: 'datetime', labelTh: 'ลูกค้าส่งข้อความล่าสุด', comparators: DATETIME }),
  f({ key: 'conversation.tags', scope: 'conversation', type: 'tags', labelTh: 'แท็กแชท', comparators: TAGS }),
  f({
    key: 'conversation.marketing_opted_out',
    scope: 'conversation',
    type: 'boolean',
    labelTh: 'ยกเลิกรับข่าวสาร (บัญชีแชทนี้หรือผู้ติดต่อ)',
    comparators: BOOL,
  }),
  f({ key: 'conversation.contact_linked', scope: 'conversation', type: 'boolean', labelTh: 'เชื่อมกับผู้ติดต่อแล้ว', comparators: BOOL }),
  f({ key: 'conversation.folder', scope: 'conversation', type: 'folder', refType: 'folder', labelTh: 'แชทอยู่ในหมวด', comparators: ['in_folder'] }),
];

export const FILTER_FIELD_MAP: ReadonlyMap<string, FieldDef> = new Map(FILTER_FIELDS.map((d) => [d.key, d]));

export type FilterNode =
  | { type: 'group'; op: 'and' | 'or'; children: FilterNode[] }
  | { type: 'not'; child: FilterNode }
  | { type: 'condition'; field: string; cmp: Comparator; value?: unknown }
  | { type: 'scope'; scope: FieldScope; where: FilterNode };

export const filterNodeSchema: z.ZodType<FilterNode> = z.lazy(() =>
  z.discriminatedUnion('type', [
    z.object({ type: z.literal('group'), op: z.enum(['and', 'or']), children: z.array(filterNodeSchema).max(50) }),
    z.object({ type: z.literal('not'), child: filterNodeSchema }),
    z.object({ type: z.literal('condition'), field: z.string().max(80), cmp: z.enum(COMPARATORS), value: z.unknown().optional() }),
    z.object({ type: z.literal('scope'), scope: z.enum(TARGET_TYPES), where: filterNodeSchema }),
  ]),
) as z.ZodType<FilterNode>;

export interface FilterIssue {
  path: string;
  messageTh: string;
}

export const FILTER_LIMITS = { maxDepth: 10, maxNodes: 100, maxListItems: 500 } as const;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Which scope switches are allowed from a root target. */
const ALLOWED_SWITCHES: Record<TargetType, readonly FieldScope[]> = {
  contact: ['registration', 'conversation'],
  registration: ['contact'],
  conversation: ['contact', 'registration'],
};
export const FILTER_SCOPE_SWITCHES: Readonly<Record<TargetType, readonly FieldScope[]>> = ALLOWED_SWITCHES;

export function validateFilter(input: unknown, target: TargetType): { ok: true; node: FilterNode } | { ok: false; issues: FilterIssue[] } {
  const parsed = filterNodeSchema.safeParse(input);
  if (!parsed.success) {
    return { ok: false, issues: parsed.error.issues.map((i) => ({ path: i.path.join('.'), messageTh: `โครงสร้างเงื่อนไขไม่ถูกต้อง (${i.message})` })) };
  }
  const issues: FilterIssue[] = [];
  let nodes = 0;

  const walk = (node: FilterNode, path: string, scope: FieldScope, depth: number, switched: boolean) => {
    nodes += 1;
    if (depth > FILTER_LIMITS.maxDepth) {
      issues.push({ path, messageTh: `เงื่อนไขซ้อนลึกเกิน ${FILTER_LIMITS.maxDepth} ชั้น` });
      return;
    }
    if (nodes > FILTER_LIMITS.maxNodes) {
      if (nodes === FILTER_LIMITS.maxNodes + 1) issues.push({ path, messageTh: `จำนวนเงื่อนไขเกิน ${FILTER_LIMITS.maxNodes}` });
      return;
    }
    switch (node.type) {
      case 'group':
        if (node.children.length === 0) issues.push({ path, messageTh: 'กลุ่มเงื่อนไขว่าง' });
        node.children.forEach((c, i) => walk(c, `${path}.children.${i}`, scope, depth + 1, switched));
        return;
      case 'not':
        walk(node.child, `${path}.child`, scope, depth + 1, switched);
        return;
      case 'scope':
        if (switched || scope !== target || !ALLOWED_SWITCHES[target].includes(node.scope)) {
          issues.push({ path, messageTh: `ไม่สามารถใช้กลุ่มเงื่อนไขแบบ "${node.scope}" ที่ตำแหน่งนี้` });
          return;
        }
        walk(node.where, `${path}.where`, node.scope, depth + 1, true);
        return;
      case 'condition': {
        const def = FILTER_FIELD_MAP.get(node.field);
        if (!def) {
          issues.push({ path, messageTh: `ไม่รู้จักฟิลด์ ${node.field}` });
          return;
        }
        if (def.scope !== scope) {
          issues.push({
            path,
            messageTh:
              def.scope === 'registration'
                ? `ฟิลด์ "${def.labelTh}" ต้องอยู่ในกลุ่ม "ใบสมัครเดียวกัน"`
                : `ฟิลด์ "${def.labelTh}" ใช้ในตำแหน่งนี้ไม่ได้`,
          });
          return;
        }
        if (!def.comparators.includes(node.cmp)) {
          issues.push({ path, messageTh: `ฟิลด์ "${def.labelTh}" ไม่รองรับตัวเปรียบเทียบ ${node.cmp}` });
          return;
        }
        const msg = checkValue(def, node.cmp, node.value);
        if (msg) issues.push({ path, messageTh: `${def.labelTh}: ${msg}` });
      }
    }
  };

  walk(parsed.data, 'root', target, 0, false);
  return issues.length ? { ok: false, issues } : { ok: true, node: parsed.data };
}

function checkValue(def: FieldDef, cmp: Comparator, value: unknown): string | null {
  if (cmp === 'is_empty' || cmp === 'not_empty' || cmp === 'is_true' || cmp === 'is_false') {
    return value === undefined || value === null ? null : 'ตัวเปรียบเทียบนี้ไม่ต้องมีค่า';
  }
  if (cmp === 'within_last_days' || cmp === 'older_than_days') {
    return Number.isInteger(value) && (value as number) >= 1 && (value as number) <= 3650 ? null : 'จำนวนวันต้องเป็น 1-3650';
  }
  if (def.type === 'route') {
    const v = value as Partial<SendRouteValue> | null;
    const ok =
      v &&
      typeof v === 'object' &&
      typeof v.accountId === 'string' &&
      UUID_RE.test(v.accountId) &&
      Array.isArray(v.allowed) &&
      v.allowed.length > 0 &&
      v.allowed.length <= 50 &&
      v.allowed.every((id) => typeof id === 'string' && UUID_RE.test(id)) &&
      (v.prefer === 'line' || v.prefer === 'window') &&
      (v.everywhere === undefined || typeof v.everywhere === 'boolean');
    return ok ? null : 'ต้องระบุบัญชี ช่องทางที่เลือก และลำดับการส่ง';
  }
  if (cmp === 'in_folder') {
    const v = value as { folderId?: unknown; includeChildren?: unknown } | null;
    return v && typeof v === 'object' && typeof v.folderId === 'string' && UUID_RE.test(v.folderId) && typeof v.includeChildren === 'boolean'
      ? null
      : 'ต้องระบุหมวดและตัวเลือกรวมหมวดย่อย';
  }
  const listCmp = cmp === 'in' || cmp === 'not_in';
  if (listCmp) {
    if (!Array.isArray(value) || value.length === 0) return 'ต้องเลือกอย่างน้อย 1 ค่า';
    if (value.length > FILTER_LIMITS.maxListItems) return `เลือกได้ไม่เกิน ${FILTER_LIMITS.maxListItems} ค่า`;
    for (const item of value) {
      const m = checkScalar(def, item);
      if (m) return m;
    }
    return null;
  }
  if (cmp === 'between') {
    if (!Array.isArray(value) || value.length !== 2) return 'ต้องระบุค่าเริ่มต้นและค่าสิ้นสุด';
    return checkScalar(def, value[0]) ?? checkScalar(def, value[1]);
  }
  return checkScalar(def, value);
}

function checkScalar(def: FieldDef, value: unknown): string | null {
  switch (def.type) {
    case 'id':
      return typeof value === 'string' && UUID_RE.test(value) ? null : 'รหัสอ้างอิงไม่ถูกต้อง';
    case 'text':
    case 'tags':
      return typeof value === 'string' && value.trim().length > 0 && value.length <= 200 ? null : 'ข้อความต้องยาว 1-200 ตัวอักษร';
    case 'phone':
      return typeof value === 'string' && value.replace(/\D/g, '').length >= 3 ? null : 'ต้องมีตัวเลขอย่างน้อย 3 หลัก';
    case 'enum':
      return typeof value === 'string' && (def.enumValues ?? []).includes(value) ? null : 'ค่าที่เลือกไม่ถูกต้อง';
    case 'money':
      return Number.isSafeInteger(value) && (value as number) >= 0 ? null : 'จำนวนเงินต้องเป็นสตางค์จำนวนเต็มไม่ติดลบ';
    case 'number':
      return typeof value === 'number' && Number.isFinite(value) ? null : 'ต้องเป็นตัวเลข';
    case 'datetime':
      return typeof value === 'string' && !Number.isNaN(Date.parse(value)) ? null : 'วันเวลาไม่ถูกต้อง';
    case 'date':
      return typeof value === 'string' && DATE_RE.test(value) ? null : 'วันที่ต้องเป็นรูปแบบ YYYY-MM-DD';
    case 'boolean':
    case 'folder':
    case 'route':
      return null;
  }
}

/** Convenience builders (used by UI presets and tests). */
export const F = {
  and: (...children: FilterNode[]): FilterNode => ({ type: 'group', op: 'and', children }),
  or: (...children: FilterNode[]): FilterNode => ({ type: 'group', op: 'or', children }),
  not: (child: FilterNode): FilterNode => ({ type: 'not', child }),
  cond: (field: string, cmp: Comparator, value?: unknown): FilterNode => ({ type: 'condition', field, cmp, value }),
  registration: (where: FilterNode): FilterNode => ({ type: 'scope', scope: 'registration', where }),
  contact: (where: FilterNode): FilterNode => ({ type: 'scope', scope: 'contact', where }),
  conversation: (where: FilterNode): FilterNode => ({ type: 'scope', scope: 'conversation', where }),
};
